import type { IExternalFormMultiselectItem } from 'components/ExternalFormMultiselect';
import { ExternalFormMultiselect } from 'components/ExternalFormMultiselect';
import { ModalDialog } from 'components/ModalDialog';
import type { PriceListUerRowBase } from 'gql/graphql';
import { RatioTypeEnum } from 'gql/graphql';
import { cnMixSpace } from 'uikit';

import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ICheckedChangeItem } from './hooks/usePriceListUerForTechPartRatioColumns';
import { PriceListUerForConstraintRatioTable } from './PriceListUerForConstraintRatioTable';
import { PriceListUerForTechPartRatioTable } from './PriceListUerForTechPartRatioTable';

import Styles from './ApplicationAreaSelector.module.css';

export interface IRatioSelectorProps {
    appliesToUerItems: PriceListUerRowBase[];
    ratioGroup: PriceListUerRowBase;
    label?: string;
    ref?: React.MutableRefObject<null>;
    disabled?: boolean;
    coefficientType: RatioTypeEnum;
    onChange?: (
        appliesToUerIds: number[],
        ratioGroupId: number | null,
        appliesToUerItems: IExternalFormMultiselectItem[],
        ratioGroup: null | IExternalFormMultiselectItem,
    ) => void;
    status: 'alert' | 'warning' | 'success' | undefined;
    disabledIds: number[];
    tooltipText?: string;
}

export const ApplicationAreaSelector: React.FC<IRatioSelectorProps> = (props) => {
    const {
        label,
        ref,
        disabled,
        coefficientType,
        status,
        onChange,
        appliesToUerItems,
        ratioGroup,
        disabledIds,
        tooltipText,
    } = props;
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [selectedItems, setSelectedItems] = useState<IExternalFormMultiselectItem[]>([]);

    const isTechPart = coefficientType === RatioTypeEnum.TechPartCoef;
    const title = `Добавление области применения коэффициента ${isTechPart ? 'технической части' : 'стесненности'}`;

    const checkedIds = useMemo(() => selectedItems.map((i) => i.id), [selectedItems]);

    const getInitialState = useCallback(() => {
        if (isTechPart) {
            setSelectedItems(appliesToUerItems.map((i) => ({ id: i.id, label: i.code, data: i })));
        } else if (ratioGroup) {
            setSelectedItems([{ id: ratioGroup.sectionId, label: ratioGroup.sectionName, data: ratioGroup }]);
        } else {
            setSelectedItems([]);
        }
    }, [appliesToUerItems, isTechPart, ratioGroup]);

    useEffect(() => {
        getInitialState();
    }, [getInitialState]);

    const onSearchOpen = useCallback(() => {
        setIsSearchOpen(true);
    }, []);

    const onSearchClose = useCallback(() => {
        getInitialState();
        setIsSearchOpen(false);
    }, [getInitialState]);

    const onSearchFormSubmit = useCallback(() => {
        onChange?.(
            selectedItems.map((i) => i.id),
            selectedItems.length ? selectedItems[0].id : null,
            selectedItems,
            selectedItems.length ? selectedItems[0] : null,
        );
        onSearchClose();
    }, [onChange, onSearchClose, selectedItems]);

    const onMultiselectChange = useCallback(
        (newSelectedItems: IExternalFormMultiselectItem[]) => {
            onChange?.(
                newSelectedItems?.length ? newSelectedItems.map((i) => i.id) : [],
                newSelectedItems?.length ? newSelectedItems[0].id : null,
                newSelectedItems || [],
                newSelectedItems?.length ? newSelectedItems[0] : null,
            );
        },
        [onChange],
    );

    const onRowCheckedChange = useCallback(
        (changeState: ICheckedChangeItem[]) => {
            const checked = changeState.filter((v) => v.isChecked);
            const idsToRemove = changeState.map((c) => c.item.id);
            const rest = selectedItems.filter((i) => !idsToRemove.includes(i.id));

            setSelectedItems([...checked.map((c) => c.item), ...rest]);
        },
        [selectedItems],
    );

    const btnConfirmDisabled = useMemo(
        () => checkedIds?.length === disabledIds?.length && checkedIds?.every((id) => disabledIds?.includes(id)),
        [checkedIds, disabledIds],
    );

    return (
        <ExternalFormMultiselect
            disabled={disabled}
            refCheckBtn={ref}
            label={label}
            labelPosition="left"
            onChange={onMultiselectChange}
            onSearchFormOpen={onSearchOpen}
            selectedItems={selectedItems}
            status={status}
            disabledIds={disabledIds}
            tooltipText={tooltipText}
            countTitleType="расценок"
        >
            <ModalDialog
                isOpen={isSearchOpen}
                title={title}
                onConfirm={onSearchFormSubmit}
                onClose={onSearchClose}
                withTitleCloseButton
                btnConfirmLabel="Добавить"
                className={Styles.applicationAreaSelectorDialog}
                bodyMinHeight="490px"
                btnConfirmDisabled={btnConfirmDisabled}
            >
                <div className={`${Styles.table} ${cnMixSpace({ p: 'xl' })}`}>
                    {isTechPart && (
                        <PriceListUerForTechPartRatioTable
                            checkedIds={checkedIds}
                            onRowCheckedChange={onRowCheckedChange}
                            disabledIds={disabledIds}
                        />
                    )}
                    {!isTechPart && (
                        <PriceListUerForConstraintRatioTable
                            selectedId={checkedIds?.[0]}
                            onRowSelectChange={onRowCheckedChange}
                        />
                    )}
                </div>
            </ModalDialog>
        </ExternalFormMultiselect>
    );
};

ApplicationAreaSelector.displayName = 'ApplicationAreaSelector';
